import React from 'react';

/**
 * SelfHealingClip
 * Looping SVG animation that visualises a workflow with self-healing:
 *  1. A packet flows through Source → Validate → Process → Deliver
 *  2. The "Process" node faults (red), an anomaly badge appears
 *  3. A "Self-Heal" agent activates, the node re-routes & turns green
 *  4. The flow resumes — loop forever
 *
 * Pure CSS keyframes — no JS, no external assets.
 */
const SelfHealingClip = () => {
  return (
    <div className="relative w-full" data-testid="self-healing-clip">
      <style>{`
        @keyframes shc-packet-1 {
          0%   { offset-distance: 0%;   opacity: 0; }
          5%   { opacity: 1; }
          35%  { offset-distance: 100%; opacity: 1; }
          40%  { opacity: 0; }
          100% { offset-distance: 100%; opacity: 0; }
        }
        @keyframes shc-packet-2 {
          0%, 60% { offset-distance: 0%; opacity: 0; }
          65%     { opacity: 1; }
          95%     { offset-distance: 100%; opacity: 1; }
          100%    { offset-distance: 100%; opacity: 0; }
        }
        @keyframes shc-fault {
          0%, 32%   { opacity: 0; transform: scale(0.6); }
          36%, 50%  { opacity: 1; transform: scale(1); }
          54%       { opacity: 0; transform: scale(0.6); }
          100%      { opacity: 0; }
        }
        @keyframes shc-node-process {
          0%, 32%  { fill: #0b1220; stroke: #1f2937; }
          38%, 52% { fill: #2a0a10; stroke: #ef4444; }
          58%, 100%{ fill: #052e1a; stroke: #22c55e; }
        }
        @keyframes shc-heal-ring {
          0%, 50%  { opacity: 0; r: 22; }
          55%      { opacity: 0.9; r: 22; }
          75%      { opacity: 0; r: 48; }
          100%     { opacity: 0; r: 48; }
        }
        @keyframes shc-heal-badge {
          0%, 48%  { opacity: 0; transform: translateY(4px); }
          54%, 78% { opacity: 1; transform: translateY(0); }
          84%, 100%{ opacity: 0; transform: translateY(-4px); }
        }
        @keyframes shc-pulse-dot {
          0%, 100% { opacity: 0.35; }
          50%      { opacity: 1; }
        }
        @keyframes shc-scan {
          0%   { transform: translateY(-100%); opacity: 0; }
          15%  { opacity: 0.6; }
          85%  { opacity: 0.6; }
          100% { transform: translateY(100%); opacity: 0; }
        }
        .shc-packet { offset-rotate: 0deg; will-change: offset-distance, opacity; }
        .shc-packet-1 { animation: shc-packet-1 6s linear infinite; }
        .shc-packet-2 { animation: shc-packet-2 6s linear infinite; }
        .shc-fault    { animation: shc-fault 6s ease-in-out infinite; transform-origin: 290px 96px; transform-box: fill-box; }
        .shc-process-rect { animation: shc-node-process 6s ease-in-out infinite; }
        .shc-heal-ring  { animation: shc-heal-ring 6s ease-out infinite; }
        .shc-heal-badge { animation: shc-heal-badge 6s ease-in-out infinite; transform-origin: center; }
        .shc-dot { animation: shc-pulse-dot 2.2s ease-in-out infinite; }
        .shc-scan { animation: shc-scan 5s ease-in-out infinite; }
      `}</style>

      {/* Card wrapper */}
      <div className="relative rounded-2xl border border-neutral-800 bg-gradient-to-br from-neutral-950 via-black to-neutral-950 p-5 overflow-hidden">
        {/* Top status bar */}
        <div className="flex items-center justify-between text-[11px]">
          <div className="flex items-center gap-2 text-neutral-400">
            <span className="w-1.5 h-1.5 rounded-full bg-blue-400 shc-dot" />
            <span className="uppercase tracking-[0.18em]">Live workflow · self-healing</span>
          </div>
          <div className="flex gap-1.5">
            <span className="w-1.5 h-1.5 rounded-full bg-neutral-700" />
            <span className="w-1.5 h-1.5 rounded-full bg-neutral-700" />
            <span className="w-1.5 h-1.5 rounded-full bg-neutral-700" />
          </div>
        </div>

        {/* Soft grid background inside card */}
        <div aria-hidden className="absolute inset-0 opacity-[0.18] pointer-events-none" style={{
          backgroundImage: 'linear-gradient(to right, rgba(255,255,255,0.06) 1px, transparent 1px), linear-gradient(to bottom, rgba(255,255,255,0.06) 1px, transparent 1px)',
          backgroundSize: '28px 28px',
          maskImage: 'radial-gradient(ellipse at center, black 50%, transparent 90%)',
        }} />
        {/* Scanning beam */}
        <div aria-hidden className="absolute inset-x-0 top-0 h-full pointer-events-none">
          <div className="shc-scan absolute inset-x-0 h-24" style={{
            background: 'linear-gradient(to bottom, transparent, rgba(59,130,246,0.18), transparent)',
            filter: 'blur(6px)',
          }} />
        </div>

        {/* SVG diagram */}
        <svg viewBox="0 0 460 230" className="relative w-full h-auto mt-3" role="img" aria-label="Self-healing workflow animation">
          <defs>
            {/* Connector path used by packets */}
            <path id="shc-flow" d="M 60 96 L 170 96 L 290 96 L 400 96" />

            {/* Glow filter for packet */}
            <filter id="shc-glow" x="-50%" y="-50%" width="200%" height="200%">
              <feGaussianBlur stdDeviation="2.4" result="b" />
              <feMerge>
                <feMergeNode in="b" />
                <feMergeNode in="SourceGraphic" />
              </feMerge>
            </filter>

            <linearGradient id="shc-line" x1="0" x2="1" y1="0" y2="0">
              <stop offset="0%"  stopColor="#1e3a8a" />
              <stop offset="50%" stopColor="#3b82f6" />
              <stop offset="100%" stopColor="#1e3a8a" />
            </linearGradient>
          </defs>

          {/* Connector base line */}
          <line x1="60"  y1="96" x2="400" y2="96" stroke="url(#shc-line)" strokeWidth="2" strokeDasharray="4 4" opacity="0.55" />

          {/* Nodes */}
          {/* Source */}
          <g>
            <rect x="20"  y="76" width="80" height="40" rx="10" fill="#0b1220" stroke="#1f2937" />
            <text x="60"  y="100" textAnchor="middle" fill="#cbd5e1" fontSize="11" fontFamily="ui-sans-serif, system-ui">Source</text>
            <text x="60"  y="62"  textAnchor="middle" fill="#64748b" fontSize="9"  fontFamily="ui-sans-serif, system-ui">ERP · CRM</text>
          </g>

          {/* Validate */}
          <g>
            <rect x="130" y="76" width="80" height="40" rx="10" fill="#0b1220" stroke="#1f2937" />
            <text x="170" y="100" textAnchor="middle" fill="#cbd5e1" fontSize="11" fontFamily="ui-sans-serif, system-ui">Validate</text>
            <text x="170" y="62"  textAnchor="middle" fill="#64748b" fontSize="9"  fontFamily="ui-sans-serif, system-ui">Rules · Schema</text>
          </g>

          {/* Process (the node that faults) */}
          <g>
            <circle className="shc-heal-ring" cx="290" cy="96" r="22" fill="none" stroke="#3b82f6" strokeWidth="2" />
            <rect className="shc-process-rect" x="250" y="76" width="80" height="40" rx="10" strokeWidth="1.5" />
            <text x="290" y="100" textAnchor="middle" fill="#e2e8f0" fontSize="11" fontFamily="ui-sans-serif, system-ui">Process</text>
            <text x="290" y="62"  textAnchor="middle" fill="#64748b" fontSize="9"  fontFamily="ui-sans-serif, system-ui">Transform</text>

            {/* Fault badge */}
            <g className="shc-fault">
              <rect x="266" y="34" width="48" height="18" rx="9" fill="#7f1d1d" stroke="#ef4444" />
              <circle cx="276" cy="43" r="2.4" fill="#fecaca" />
              <text x="306" y="46" textAnchor="end" fill="#fecaca" fontSize="9" fontFamily="ui-sans-serif, system-ui">FAULT</text>
            </g>

            {/* Heal badge */}
            <g className="shc-heal-badge">
              <rect x="260" y="34" width="60" height="18" rx="9" fill="#064e3b" stroke="#22c55e" />
              <circle cx="270" cy="43" r="2.4" fill="#bbf7d0" />
              <text x="314" y="46" textAnchor="end" fill="#bbf7d0" fontSize="9" fontFamily="ui-sans-serif, system-ui">HEALED</text>
            </g>
          </g>

          {/* Deliver */}
          <g>
            <rect x="370" y="76" width="80" height="40" rx="10" fill="#0b1220" stroke="#1f2937" />
            <text x="410" y="100" textAnchor="middle" fill="#cbd5e1" fontSize="11" fontFamily="ui-sans-serif, system-ui">Deliver</text>
            <text x="410" y="62"  textAnchor="middle" fill="#64748b" fontSize="9"  fontFamily="ui-sans-serif, system-ui">Downstream</text>
          </g>

          {/* Self-Heal Agent (bottom) */}
          <g>
            <line x1="290" y1="116" x2="290" y2="160" stroke="#3b82f6" strokeWidth="1.2" strokeDasharray="3 3" opacity="0.6" />
            <rect x="222" y="160" width="136" height="44" rx="10" fill="#0b1220" stroke="#1e3a8a" />
            <circle cx="240" cy="182" r="7" fill="#1e3a8a" stroke="#3b82f6" />
            <circle cx="240" cy="182" r="2.6" fill="#bfdbfe" className="shc-dot" />
            <text x="256" y="178" fill="#dbeafe" fontSize="11" fontFamily="ui-sans-serif, system-ui">Self-Heal Agent</text>
            <text x="256" y="192" fill="#64748b" fontSize="9"  fontFamily="ui-sans-serif, system-ui">retry · reroute · remediate</text>
          </g>

          {/* Packet 1 */}
          <circle
            className="shc-packet shc-packet-1"
            r="4.5"
            fill="#60a5fa"
            filter="url(#shc-glow)"
            style={{ offsetPath: "path('M 60 96 L 170 96 L 290 96 L 400 96')" }}
          />
          {/* Packet 2 (after heal) */}
          <circle
            className="shc-packet shc-packet-2"
            r="4.5"
            fill="#34d399"
            filter="url(#shc-glow)"
            style={{ offsetPath: "path('M 60 96 L 170 96 L 290 96 L 400 96')" }}
          />
        </svg>

        {/* Legend */}
        <div className="mt-3 flex items-center justify-between text-[10.5px] text-neutral-500">
          <div className="flex items-center gap-3">
            <span className="inline-flex items-center gap-1.5"><span className="w-1.5 h-1.5 rounded-full bg-blue-400" /> Detect</span>
            <span className="inline-flex items-center gap-1.5"><span className="w-1.5 h-1.5 rounded-full bg-red-400" /> Fault</span>
            <span className="inline-flex items-center gap-1.5"><span className="w-1.5 h-1.5 rounded-full bg-emerald-400" /> Healed</span>
          </div>
          <span className="tabular-nums">avg recovery · 1.2s</span>
        </div>
      </div>
    </div>
  );
};

export default SelfHealingClip;
